import { get, size, has, isEmpty } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import CallCard from '../CallCard';
import CallListSkeleton from '../CallListSkeleton';
import CallSkeleton from '../CallSkeleton';
import DatePicker from '../../../DatePicker';
import InfiniteTabs from '../../Common/InfiniteTabs';
import Tab from '../../../Tab';
import ToggleButtonsGroup from '../../../ToggleButtonsGroup';

import {
  IncomingCall,
  OutgoingCall,
  IncomingCallUnanswered,
} from '@icons/communication';
import { ICallCard } from '../CallCard/types';
import { InfiniteTabsProps } from '@components/CRM/Common/InfiniteTabs/types';
import { DateValueType } from '@components/DatePicker/types';

const StyledIncomingIcon = styled(IncomingCall)`
  width: 17px;
  height: 17px;
  height: 15px;
  margin-top: 3px;

  > * {
    fill: #00c253;
    stroke: #00c253;
  }
`;

const StyledOutgoingIcon = styled(OutgoingCall)`
  height: 15px;
  margin-top: 3px;

  > * {
    fill: #00c253;
    stroke: #00c253;
  }
`;

const UnansweredIncomingIcon = styled(IncomingCallUnanswered)`
  width: 18px;
  height: 18px;
`;

const ICONS = [
  { Icon: StyledIncomingIcon, value: 'incoming' },
  { Icon: StyledOutgoingIcon, value: 'outgoing' },
  { Icon: UnansweredIncomingIcon, value: 'unanswered' },
];
const ITEM_SIZE = 133;
const PAGE_SIZE_DEFAULT = 20;
const THRESHOLD_DEFAULT = 5;

export interface CallListProps {
  className?: string;
  activeId?: string;
  calls: ICallCard[];
  hasNextPage?: boolean;
  isNextPageLoading?: boolean;
  minimumBatchSize?: InfiniteTabsProps['minimumBatchSize'];
  threshold?: InfiniteTabsProps['threshold'];
  onFilter?: ({
    dateRange,
    type,
  }: {
    dateRange: CallListState['dateRange'];
    type: CallListState['type'];
  }) => void;
  onListen: ICallCard['onListen'];
  onLoadRequest: InfiniteTabsProps['onLoadRequest'];
  onSelect: InfiniteTabsProps['onSelect'];
}

interface CallListState {
  dateRange: DateValueType | null;
  type: string | null;
}

class CallList extends React.PureComponent<CallListProps, CallListState> {
  static defaultProps = {
    activeId: null,
    calls: [],
    className: '',
    hasNextPage: false,
    isNextPageLoading: false,
    minimumBatchSize: PAGE_SIZE_DEFAULT,
    threshold: THRESHOLD_DEFAULT,
  };
  static getItemKey(index, calls) {
    return get(calls, `${index}.call.id`) || index;
  }

  constructor(props) {
    super(props);
    this.state = {
      dateRange: null,
      type: null,
    };

    this.handleChangeDate = this.handleChangeDate.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleLoadPage = this.handleLoadPage.bind(this);
    this.handleToggleTypes = this.handleToggleTypes.bind(this);
    this.isItemLoaded = this.isItemLoaded.bind(this);
  }

  handleChangeDate(dateRange) {
    this.setState({ dateRange }, this.handleFilterChange);
  }

  handleFilterChange() {
    const { onFilter } = this.props;
    const { type, dateRange } = this.state;
    onFilter?.({ dateRange, type });
  }

  handleLoadPage(startIndex, stopIndex) {
    const { isNextPageLoading } = this.props;
    if (isNextPageLoading) {
      return;
    }

    const { onLoadRequest } = this.props;
    onLoadRequest?.(startIndex, stopIndex);
  }

  handleToggleTypes({ detail }) {
    this.setState({ type: detail.value[0] }, this.handleFilterChange);
  }

  getItemCount() {
    const { calls, hasNextPage } = this.props;
    const countedSize = size(calls);
    return hasNextPage ? countedSize + 1 : countedSize;
  }

  isItemLoaded(index) {
    const { calls } = this.props;
    return has(calls, `${index}.call.id`);
  }

  errorRenderer() {
    const { isNextPageLoading } = this.props;

    return isNextPageLoading ? (
      <StyledCallListSkeleton />
    ) : (
      <StyledEmpty>Нет звонков.</StyledEmpty>
    );
  }

  render() {
    const {
      activeId,
      calls,
      hasNextPage,
      minimumBatchSize,
      onListen,
      onLoadRequest,
      onSelect,
      threshold,
      ...props
    } = this.props;
    const { dateRange, type } = this.state;

    delete props.onFilter; // Fix test error.

    return (
      <StyledCallList {...props}>
        <StyledFilters>
          <ToggleButtonsGroup
            key={type}
            onChange={this.handleToggleTypes}
            values={[type]}
          >
            {ICONS.map(({ Icon, value }) => (
              <StyledButton key={value} value={value}>
                <Icon />
              </StyledButton>
            ))}
          </ToggleButtonsGroup>
          <StyledDatePicker
            dateFormat="d MMM yy"
            multiple
            onChange={this.handleChangeDate}
            required={false}
            value={dateRange}
          />
        </StyledFilters>
        {hasNextPage || !isEmpty(calls) ? (
          <StyledInfiniteTabs
            isItemLoaded={this.isItemLoaded}
            itemCount={this.getItemCount()}
            itemData={calls}
            itemKey={CallList.getItemKey}
            itemSize={ITEM_SIZE}
            minimumBatchSize={minimumBatchSize}
            onLoadRequest={this.handleLoadPage}
            onSelect={onSelect}
            threshold={threshold}
            value={activeId}
          >
            {({ data, index, style }) => {
              if (!this.isItemLoaded(index)) {
                return (
                  <Tab style={style}>
                    <StyledCallSkeleton />
                  </Tab>
                );
              }

              const id = CallList.getItemKey(index, data);

              return (
                <Tab style={style} value={id}>
                  <StyledCallCard {...data[index]} onListen={onListen} />
                </Tab>
              );
            }}
          </StyledInfiniteTabs>
        ) : (
          this.errorRenderer()
        )}
      </StyledCallList>
    );
  }
}

const StyledCallList = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledFilters = styled.div`
  display: flex;
  flex-shrink: 0;
  height: 36px;
  margin-bottom: 10px;
  padding-right: 10px;
`;

const StyledInfiniteTabs = styled(InfiniteTabs)`
  flex-grow: 1;
  overflow: hidden;
`;

const StyledCallCard = styled(CallCard)`
  border: none;
  width: 100%;
`;

const StyledEmpty = styled.div`
  color: #4d4d4d;
  height: 100%;
  padding-top: 85px;
  text-align: center;
`;

const StyledButton = styled(Button)`
  align-items: center;
  display: flex;

  svg {
    height: 16px;
    margin: auto;
    width: 16px;
  }

  ${({ checked }) =>
    checked &&
    `
    background-color: #007aff;

    svg > * {
      fill: none !important;
      stroke: white !important;
    }
  `}
`;

const StyledDatePicker = styled(DatePicker)`
  margin-left: 10px;
  width: 161px;
  z-index: 1;
`;

const StyledCallListSkeleton = styled(CallListSkeleton)`
  flex-grow: 1;
  overflow: hidden;
`;

const StyledCallSkeleton = styled(CallSkeleton)`
  border: none;
  width: 100%;
`;

export default CallList;
